import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getFirestore, doc, onSnapshot, collection, query, where, getDocs, updateDoc, getDoc, arrayUnion } from "firebase/firestore";
import app from "../../firebase";
import { clearEnrolledSession } from "../../auth/roleSession";
import { buildEnrollmentWindows, formatCalendarDate, useCalendarClock } from "../../utils/announcementDates";

export default function EnrolledCourseLineUp({ student: propStudent }) {
  const todayKey = useCalendarClock();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [student, setStudent] = useState(propStudent || state?.student || null);
  const [loading, setLoading] = useState(true);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState("");
  const [courses, setCourses] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [classList, setClassList] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const classListRef = React.useRef(null);

  const [isSubmittedForTerm, setIsSubmittedForTerm] = useState(false);
  const [submittedTermKey, setSubmittedTermKey] = useState("");
  const [lockedClassList, setLockedClassList] = useState([]);
  const [announcementSettings, setAnnouncementSettings] = useState(null);
  const [reservedCounts, setReservedCounts] = useState({});
  const [enrolledCounts, setEnrolledCounts] = useState({});

  const SIDEBAR_WIDTH = 240;
  const HEADER_HEIGHT = 70;
  const PAGE_PADDING = 32;
  const CONTENT_MAX = 1200;
  const [scrollOffset, setScrollOffset] = useState(0);
  const [lastScrollTop, setLastScrollTop] = useState(0);

  const navItems = [
    { key: "home", label: "Home", path: "/enrolled/dashboard" },
    { key: "pre", label: "Pre-Registration", path: "/enrolled/pre-registration" },
    { key: "curriculum", label: "Curriculum Overview", path: "/enrolled/curriculum-overview" },
    { key: "schedule", label: "Class Schedule", path: "/enrolled/class-schedule" },
    { key: "final", label: "COR", path: "/enrolled/cor" },
  ];

  const fullName = student ? `${student.firstName || ""} ${student.lastName || ""}`.trim() : "Enrolled Student";
  const profilePicUrl = student?.profilePicUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(fullName || "Enrolled")}`;

  const handleProfile = (action) => {
    setProfileMenuOpen(false);
    if (action === "logout") {
      clearEnrolledSession();
      navigate("/enrolled/signin");
      return;
    }
  };

  const formatDate = (dateStr) => {
    return formatCalendarDate(dateStr);
  };

  const enrolledWindowInfo = buildEnrollmentWindows(announcementSettings || {}, todayKey);

  // Load student from Firebase
  useEffect(() => {
    if (student?.id) {
      const db = getFirestore(app);
      const studentRef = doc(db, "students", student.id);
      const unsubscribe = onSnapshot(studentRef, (snap) => {
        if (snap.exists()) {
          setStudent({ id: snap.id, ...snap.data() });
        }
        setLoading(false);
      });
      return () => unsubscribe();
    } else {
      setLoading(false);
    }
  }, [student?.id]);

  useEffect(() => {
    const db = getFirestore(app);
    const ref = doc(db, "systemSettings", "announcement");
    const unsubscribe = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        setAnnouncementSettings(snap.data());
      }
    });
    return () => unsubscribe();
  }, []);

  // Calculate next term/year
  const getNextTermAndYear = () => {
    if (!student) return { nextYear: "", nextTerm: "", currentYear: "", currentTerm: "" };

    const currentYear = student.preRegistration?.entryLevel || student.yearLevel || "First Year";
    const currentTerm = student.preRegistration?.semester || student.semester || "First Semester";

    const yearLevels = ["First Year", "Second Year", "Third Year", "Fourth Year"];
    const semesters = ["First Semester", "Second Semester"];

    let nextYear = currentYear;
    let nextTerm = currentTerm;

    const currentYearIdx = yearLevels.indexOf(currentYear);
    const currentTermIdx = semesters.indexOf(currentTerm);

    if (currentTermIdx === 0) {
      nextTerm = "Second Semester";
    } else {
      nextTerm = "First Semester";
      if (currentYearIdx < yearLevels.length - 1) {
        nextYear = yearLevels[currentYearIdx + 1];
      }
    }

    return { nextYear, nextTerm, currentYear, currentTerm };
  };

  const { nextYear, nextTerm } = getNextTermAndYear();

  const groupSchedulesToClassList = (schedules = []) => {
    const map = {};
    schedules.forEach(s => {
      const key = `${s.courseId}|${s.section}|${s.semester}|${s.schoolYear}`;
      if (!map[key]) {
        map[key] = {
          courseId: s.courseId,
          courseName: s.courseName,
          section: s.section,
          units: s.units,
          semester: s.semester,
          schoolYear: s.schoolYear,
          meetings: []
        };
      }
      map[key].meetings.push({
        day: s.day,
        time: s.time,
        room: s.room
      });
    });
    return Object.values(map);
  };

  useEffect(() => {
    const termSchedules = student?.termSchedules || {};
    const key = Object.keys(termSchedules).find(k => k.endsWith(`_${nextTerm}`));

    if (key && (termSchedules[key] || []).length > 0) {
      setIsSubmittedForTerm(true);
      setSubmittedTermKey(key);
      setLockedClassList(groupSchedulesToClassList(termSchedules[key]));
    } else {
      setIsSubmittedForTerm(false);
      setSubmittedTermKey("");
      setLockedClassList([]);
    }
  }, [student, nextTerm]);

  // Fetch sections for next term
  useEffect(() => {
    if (!student || !nextYear || !nextTerm) {
      setSections([]);
      return;
    }

    const fetchSections = async () => {
      const db = getFirestore(app);
      const schedulesRef = collection(db, "schedules");
      const q = query(
        schedulesRef,
        where("department", "==", student.program),
        where("yearLevel", "==", nextYear),
        where("semester", "==", nextTerm)
      );

      const snap = await getDocs(q);
      const uniqueSections = [...new Set(snap.docs.map(doc => doc.data().section).filter(Boolean))];
      setSections(uniqueSections);
    };

    fetchSections();
  }, [student, nextYear, nextTerm]);

  // Fetch reserved counts from all students for the selected term
  useEffect(() => {
    if (!nextTerm || !selectedSection) {
      setReservedCounts({});
      return;
    }

    const fetchReservedCounts = async () => {
      const db = getFirestore(app);
      const studentsRef = collection(db, "students");
      const studentsSnap = await getDocs(studentsRef);
      
      const counts = {};
      
      studentsSnap.docs.forEach(doc => {
        const studentData = doc.data();
        const termSchedules = studentData.termSchedules || {};
        
        // Look for schedules in any term key that ends with the current semester
        Object.keys(termSchedules).forEach(termKey => {
          if (termKey.endsWith(`_${nextTerm}`)) {
            const schedules = termSchedules[termKey] || [];
            
            // Count unique course reservations per student (deduplicate by courseId)
            const uniqueCourses = new Set();
            schedules.forEach(schedule => {
              if (schedule.courseId && schedule.section === selectedSection) {
                uniqueCourses.add(schedule.courseId);
              }
            });
            
            // Increment count for each unique course this student has
            uniqueCourses.forEach(courseId => {
              counts[courseId] = (counts[courseId] || 0) + 1;
            });
          }
        });
      });
      
      setReservedCounts(counts);
    };

    fetchReservedCounts();
  }, [nextTerm, selectedSection]);

  // Fetch enrolled counts from students with registrationStatus='enrolled'
  useEffect(() => {
    if (!nextTerm || !selectedSection) {
      setEnrolledCounts({});
      return;
    }

    const fetchEnrolledCounts = async () => {
      const db = getFirestore(app);
      const studentsRef = collection(db, "students");
      const q = query(studentsRef, where("registrationStatus", "==", "enrolled"));
      const studentsSnap = await getDocs(q);
      
      const counts = {};
      
      studentsSnap.docs.forEach(doc => {
        const studentData = doc.data();
        const termSchedules = studentData.termSchedules || {};
        
        // Look for schedules in any term key that ends with the current semester
        Object.keys(termSchedules).forEach(termKey => {
          if (termKey.endsWith(`_${nextTerm}`)) {
            const schedules = termSchedules[termKey] || [];
            
            // Count unique course enrollments per student (deduplicate by courseId)
            const uniqueCourses = new Set();
            schedules.forEach(schedule => {
              if (schedule.courseId && schedule.section === selectedSection) {
                uniqueCourses.add(schedule.courseId);
              }
            });
            
            // Increment count for each unique course this student has
            uniqueCourses.forEach(courseId => {
              counts[courseId] = (counts[courseId] || 0) + 1;
            });
          }
        });
      });
      
      setEnrolledCounts(counts);
    };

    fetchEnrolledCounts();
  }, [nextTerm, selectedSection]);

  // Fetch courses when section is selected
  useEffect(() => {
    if (!selectedSection || !student || !nextYear || !nextTerm) {
      setCourses([]);
      setSelectedCourses([]);
      return;
    }

    const fetchCourses = async () => {
      const db = getFirestore(app);
      const schedulesRef = collection(db, "schedules");
      const q = query(
        schedulesRef,
        where("department", "==", student.program),
        where("yearLevel", "==", nextYear),
        where("semester", "==", nextTerm),
        where("section", "==", selectedSection)
      );

      const snap = await getDocs(q);
      
      // Group by course to handle multiple meetings
      const coursesMap = {};
      snap.docs.forEach(doc => {
        const data = doc.data();
        const key = [data.courseId, data.courseName, data.section].join('|');
        
        if (!coursesMap[key]) {
          coursesMap[key] = {
            id: doc.id,
            courseId: data.courseId,
            courseName: data.courseName,
            section: data.section,
            units: data.units,
            type: data.type,
            department: data.department,
            yearLevel: data.yearLevel,
            semester: data.semester,
            schoolYear: data.schoolYear,
            limit: data.limit || 40,
            meetings: []
          };
        }
        
        coursesMap[key].meetings.push({
          day: data.day,
          time: data.time,
          room: data.room,
          prof: data.prof || "TBA"
        });
      });

      setCourses(Object.values(coursesMap));
      setSelectedCourses([]);
    };

    fetchCourses();
  }, [selectedSection, student, nextYear, nextTerm]);

  // Calculate reserved count for each course from database
  const getReservedCount = (courseId) => {
    return reservedCounts[courseId] || 0;
  };

  // Calculate enrolled count for each course from database
  const getEnrolledCount = (courseId) => {
    return enrolledCounts[courseId] || 0;
  };

  const handleSelectCourse = (courseId) => {
    const reserved = getReservedCount(courseId);
    const courseLimit = courses.find(c => c.courseId === courseId)?.limit || 40;

    // Don't allow selection if limit is reached
    if (reserved >= courseLimit && !selectedCourses.includes(courseId)) {
      setMessage(`Course ${courseId} has reached its limit of ${courseLimit}`);
      setTimeout(() => setMessage(""), 3000);
      return;
    }

    setSelectedCourses(prev => {
      if (prev.includes(courseId)) {
        return prev.filter(id => id !== courseId);
      } else {
        return [...prev, courseId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedCourses.length === courses.length) {
      setSelectedCourses([]);
    } else {
      const availableCourses = courses.filter(c => {
        const reserved = getReservedCount(c.courseId);
        return reserved < (c.limit || 40);
      }).map(c => c.id);
      setSelectedCourses(availableCourses);
    }
  };

  const handleAddCourses = () => {
    const newCourses = courses.filter(course => selectedCourses.includes(course.id));
    
    if (newCourses.length === 0) {
      setMessage("Please select at least one course");
      return;
    }

    const courseEntries = newCourses.map(course => ({
      courseId: course.courseId,
      courseName: course.courseName,
      section: course.section,
      units: course.units,
      type: course.type,
      department: course.department,
      yearLevel: course.yearLevel,
      semester: course.semester,
      schoolYear: course.schoolYear,
      limit: course.limit,
      meetings: course.meetings
    }));

    setClassList(prev => {
      const merged = [...prev, ...courseEntries];
      return merged.filter((item, idx, arr) =>
        arr.findIndex(i =>
          i.courseId === item.courseId &&
          i.section === item.section &&
          i.semester === item.semester &&
          i.schoolYear === item.schoolYear
        ) === idx
      );
    });

    setMessage(`Added ${newCourses.length} course(s) to class list`);
    setSelectedCourses([]);
    setTimeout(() => setMessage(""), 3000);

    setTimeout(() => {
      classListRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
  };

  const handleSubmit = async () => {
    if (classList.length === 0) {
      setMessage("Please add at least one course before submitting");
      return;
    }

    setSubmitting(true);
    setMessage("");

    try {
      const db = getFirestore(app);
      const studentRef = doc(db, "students", student.id);

      const scheduleEntries = [];
      classList.forEach(course => {
        (course.meetings || []).forEach(meeting => {
          scheduleEntries.push({
            courseId: course.courseId,
            courseName: course.courseName,
            section: course.section,
            units: course.units,
            type: course.type,
            department: course.department,
            yearLevel: course.yearLevel,
            semester: course.semester,
            schoolYear: course.schoolYear,
            day: meeting.day,
            time: meeting.time,
            room: meeting.room,
            prof: meeting.prof,
            limit: course.limit
          });
        });
      });

      const termSchoolYear = classList[0]?.schoolYear || "AY-Unknown";
      const termSemester = classList[0]?.semester || "Term-Unknown";
      const termKey = `${termSchoolYear}_${termSemester}`;

      const feesRef = doc(db, "systemSettings", "miscellaneousFees");
      const feesSnap = await getDoc(feesRef);
      const feesData = feesSnap.exists() ? feesSnap.data() : {};

      const termIsSecondSem = String(termSemester).toLowerCase().includes("second") || String(termSemester).toLowerCase().includes("2nd");
      const selectedFees = feesData.fees || [];
      const cashDiscount = termIsSecondSem
        ? (feesData.secondSemCashDiscount ?? feesData.cashDiscount ?? 0)
        : (feesData.firstSemCashDiscount ?? feesData.cashDiscount ?? 0);
      const cashWithDiscountDate = termIsSecondSem
        ? (feesData.secondSemCashWithDiscountDate || "")
        : (feesData.firstSemCashWithDiscountDate || feesData.cashWithDiscountDate || "");
      const cashWithoutDiscountDate = termIsSecondSem
        ? (feesData.secondSemCashWithoutDiscountDate || "")
        : (feesData.firstSemCashWithoutDiscountDate || feesData.cashWithoutDiscountDate || "");
      const installmentDates = termIsSecondSem
        ? (feesData.secondSemInstallmentDates || [])
        : (feesData.firstSemInstallmentDates || feesData.installmentDates || []);

      const totalUnits = classList.reduce((sum, course) => sum + (parseFloat(course.units) || 0), 0);
      const totalAmount = selectedFees.reduce((sum, fee) => {
        if (fee.perUnit !== undefined) return sum + (totalUnits * (parseFloat(fee.perUnit) || 0));
        if (fee.formula && String(fee.formula).toLowerCase().includes("units") && typeof fee.amount === "number") {
          return sum + (totalUnits * (parseFloat(fee.amount) || 0));
        }
        return sum + (parseFloat(fee.amount) || 0);
      }, 0);

      const totalWithDiscount = totalAmount - (parseFloat(cashDiscount) || 0);
      const installmentAmount = parseFloat((totalAmount / 5).toFixed(2));

      const schoolYearLabel = String(termSchoolYear).startsWith("AY") ? termSchoolYear : `AY ${termSchoolYear}`;
      const miscFeesSnapshot = {
        schoolYear: schoolYearLabel,
        semester: termSemester,
        createdAt: new Date().toISOString(),
        totalUnits,
        fees: selectedFees,
        cashDiscount,
        cashWithDiscountDate,
        cashWithoutDiscountDate,
        installmentDates,
        totalAmount,
        totalWithDiscount,
        installmentAmount
      };

      await updateDoc(studentRef, {
        [`termSchedules.${termKey}`]: scheduleEntries,
        preRegistrationStatus: "submitted",
        preRegistrationSubmittedAt: new Date().toISOString(),
        [`miscellaneousFeesHistory.${termKey}`]: arrayUnion(miscFeesSnapshot)
      });

      setMessage("✓ Course line-up submitted successfully!");
      setClassList([]);
      setSelectedCourses([]);
      setSelectedSection("");
      setTimeout(() => {
        navigate("/enrolled/payment-of-fees", { state: { student, termKey } });
      }, 2000);
    } catch (err) {
      setMessage(`Error: ${err.message}`);
      console.error("Submit error:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const totalUnits = (isSubmittedForTerm ? lockedClassList : classList)
    .reduce((sum, course) => sum + (parseFloat(course.units) || 0), 0);

  const uniqueCourseCount = (isSubmittedForTerm ? lockedClassList : classList).length;

  const handleRemoveFromClassList = (idx) => {
    setClassList(prev => prev.filter((_, i) => i !== idx));
  };

  const isEnrolledWindowOpen = !!enrolledWindowInfo.active;
  const windowForMessage = enrolledWindowInfo.active || enrolledWindowInfo.next;

  // Handle scroll for hide-on-scroll behavior
  useEffect(() => {
    const mainElement = document.querySelector('[data-scroll-container]');
    if (!mainElement) return;

    const handleScroll = () => {
      const scrollTop = mainElement.scrollTop;
      if (scrollTop > lastScrollTop) {
        setScrollOffset(Math.min(scrollTop - lastScrollTop, HEADER_HEIGHT + SIDEBAR_WIDTH));
      } else {
        setScrollOffset(Math.max(scrollTop - lastScrollTop, -(HEADER_HEIGHT + SIDEBAR_WIDTH)));
      }
      setLastScrollTop(scrollTop);
    };

    mainElement.addEventListener('scroll', handleScroll);
    return () => mainElement.removeEventListener('scroll', handleScroll);
  }, [lastScrollTop]);

  return (
    <div style={{ display: "flex", minHeight: "100vh", fontFamily: "Inter, Arial, sans-serif", background: "#efefef" }}>
      {/* Sidebar */}
      <aside style={{ width: SIDEBAR_WIDTH, background: "#0f5f3b", color: "#fff", display: "flex", flexDirection: "column", position: "fixed", height: "100vh", left: 0, top: 0, transform: `translateY(${Math.max(-scrollOffset, -HEADER_HEIGHT)}px)`, transition: "transform 0.3s ease", zIndex: 1000 }}>
        <div style={{ padding: "28px 24px", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
          <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: 1 }}>EnrollEase</div>
        </div>
        <nav style={{ paddingTop: 24, flex: 1 }}>
          {navItems.map((n) => {
            const isActive = n.key === "pre" || window.location.pathname.startsWith(n.path);
            return (
              <div
                key={n.key}
                onClick={() => navigate(n.path, { state: { student } })}
                style={{
                  padding: "14px 24px",
                  margin: "4px 16px",
                  borderRadius: 8,
                  cursor: "pointer",
                  background: isActive ? "#ff6f2e" : "transparent",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: 15,
                  transition: "all 0.2s",
                  boxShadow: isActive ? "0 2px 8px rgba(255,111,46,0.3)" : "none"
                }}
              >
                {n.label}
              </div>
            );
          })}
        </nav>
      </aside>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", marginLeft: SIDEBAR_WIDTH }}>
        {/* Header */}
        <header style={{ height: HEADER_HEIGHT, background: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", padding: `0 ${PAGE_PADDING}px`, boxSizing: "border-box", borderBottom: "1px solid #e0e0e0", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", position: "sticky", top: 0, transform: `translateY(${-Math.min(scrollOffset, HEADER_HEIGHT)}px)`, transition: "transform 0.3s ease", zIndex: 999, position: "relative" }}>
          <div style={{ color: "#0f5f3b", fontWeight: 700, fontSize: 20 }}>Course Line-up</div>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ textAlign: "right" }}>
              <div style={{ color: "#0f5f3b", fontWeight: 600, fontSize: 15 }}>{fullName}</div>
            </div>
            <img
              src={profilePicUrl}
              alt="Profile"
              style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover", cursor: "pointer", border: "3px solid #ff6f2e" }}
              onClick={() => setProfileMenuOpen(v => !v)}
            />
            {profileMenuOpen && (
              <div style={{ position: "absolute", top: HEADER_HEIGHT - 4, right: PAGE_PADDING, background: "#fff", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.13)", minWidth: 180, padding: "10px 0", zIndex: 100 }}>
                <div style={{ padding: "10px 24px", cursor: "pointer", color: "#ff6f2e", fontWeight: 600 }} onClick={() => handleProfile("logout")}>Log out</div>
              </div>
            )}
          </div>
        </header>

        {/* Main Content */}
        <main style={{ flex: 1, padding: PAGE_PADDING, boxSizing: "border-box", overflowY: "auto", background: "#efefef", marginTop: 0 }} data-scroll-container>
          <div style={{ maxWidth: CONTENT_MAX, margin: "0 auto" }}>
            {loading ? (
              <div style={{ textAlign: "center", padding: 48, color: "#666" }}>Loading course line-up data...</div>
            ) : !student ? (
              <div style={{ background: "#fff", borderRadius: 8, padding: 24, color: "#b71c1c" }}>
                Student information not found. Please go back to dashboard.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
                {!isEnrolledWindowOpen && windowForMessage && (
                  <div style={{ background: "#fff3cd", border: "1px solid #ffeeba", borderRadius: 8, padding: 16, color: "#856404" }}>
                    Enrollment window is closed. Please check Pre-Registration for the schedule.
                    <div style={{ marginTop: 6, fontSize: 13 }}>
                      Next window: AY {windowForMessage.year || "—"} {windowForMessage.semester} •{" "}
                      {formatDate(windowForMessage.start)} to {formatDate(windowForMessage.end)}
                    </div>
                  </div>
                )}

                {/* Step Indicator */}
                <div style={{ display: "flex", gap: 24 }}>
                  {["CLEARANCE", "COURSE LINE-UP", "PAYMENT OF FEES"].map((stepName, idx) => (
                    <div
                      key={stepName}
                      onClick={() => {
                        if (stepName === "CLEARANCE") navigate("/enrolled/pre-registration", { state: { student } });
                        else if (stepName === "COURSE LINE-UP") navigate("/enrolled/course-lineup", { state: { student } });
                        else if (stepName === "PAYMENT OF FEES" && isSubmittedForTerm && isEnrolledWindowOpen) {
                          navigate("/enrolled/payment-of-fees", { state: { student, termKey: submittedTermKey } });
                        }
                      }}
                      style={{
                        flex: 1,
                        padding: "16px 24px",
                        background: stepName === "COURSE LINE-UP" ? "#ff6f2e" : "#fff",
                        color: stepName === "COURSE LINE-UP" ? "#fff" : "#0f5f3b",
                        borderRadius: 8,
                        fontWeight: 700,
                        textAlign: "center",
                        cursor: stepName === "PAYMENT OF FEES" && (!isSubmittedForTerm || !isEnrolledWindowOpen) ? "not-allowed" : "pointer",
                        opacity: stepName === "PAYMENT OF FEES" && (!isSubmittedForTerm || !isEnrolledWindowOpen) ? 0.6 : 1,
                        boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
                      }}
                    >
                      {idx + 1}. {stepName}
                    </div>
                  ))}
                </div>

                {!isEnrolledWindowOpen ? null : (
                  <>
                    {/* Intro / Steps */}
                    <div style={{ background: "#fff", borderRadius: 12, padding: "24px 32px", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
                      <div style={{ fontSize: 20, fontWeight: 700, color: "#0f5f3b", marginBottom: 8 }}>
                        Course Line-up Guide
                      </div>
                      <div style={{ color: "#555", fontSize: 14, lineHeight: 1.7 }}>
                        <div><b>Step 1:</b> Choose your section.</div>
                        <div><b>Step 2:</b> Select the subjects you want to take from the table.</div>
                        <div><b>Step 3:</b> Click <b>ADD</b> to place them in your Class List.</div>
                        <div><b>Step 4:</b> Review your Class List, then click <b>SUBMIT</b>.</div>
                      </div>
                    </div>

                    {isSubmittedForTerm ? (
                      <div style={{ background: "#fff", borderRadius: 12, padding: "24px 32px", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
                        <div style={{ fontSize: 18, fontWeight: 700, color: "#0f5f3b", marginBottom: 6 }}>
                          Course Line-up Submitted
                        </div>
                        <div style={{ color: "#555", fontSize: 14, lineHeight: 1.7 }}>
                          You have already selected a Course Line-up for this term. Please proceed to
                          <b> PAYMENT OF FEES</b> for your Enrollment Fee.
                        </div>
                      </div>
                    ) : (
                      <div style={{ background: "#fff", borderRadius: 12, padding: "32px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)", marginBottom: 24 }}>
                        <div style={{ fontSize: 24, fontWeight: 700, marginBottom: 24, color: "#0f5f3b" }}>
                          Select Courses for {nextYear} - {nextTerm}
                        </div>

                        {/* Section Selection */}
                        <div style={{ marginBottom: 24 }}>
                          <label style={{ display: "block", fontWeight: 600, marginBottom: 8, color: "#222" }}>
                            Section: <span style={{ color: "red" }}>*</span>
                          </label>
                          <select
                            value={selectedSection}
                            onChange={(e) => setSelectedSection(e.target.value)}
                            style={{
                              width: "100%",
                              padding: "12px",
                              borderRadius: 6,
                              border: "1px solid #ccc",
                              fontSize: 14,
                              fontFamily: "inherit"
                            }}
                          >
                            <option value="">Select a section...</option>
                            {sections.map((section) => (
                              <option key={section} value={section}>
                                Section {section}
                              </option>
                            ))}
                          </select>
                          {sections.length === 0 && (
                            <div style={{ color: "#666", marginTop: 8, fontSize: 14 }}>
                              No sections available for {nextYear} - {nextTerm}
                            </div>
                          )}
                        </div>

                        {/* Courses Table */}
                        {selectedSection && courses.length > 0 && (
                          <div>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                              <div style={{ fontSize: 16, fontWeight: 600 }}>Available Courses ({courses.length})</div>
                              <button
                                onClick={handleSelectAll}
                                style={{
                                  background: "#ff6f2e",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: 6,
                                  padding: "8px 16px",
                                  fontWeight: 600,
                                  cursor: "pointer"
                                }}
                              >
                                {selectedCourses.length === courses.filter(c => getReservedCount(c.courseId) < (c.limit || 40)).length && selectedCourses.length > 0 ? "Deselect All" : "Select All"}
                              </button>
                            </div>

                            <div style={{ overflowX: "auto", border: "1px solid #ddd", borderRadius: 8, marginBottom: 16 }}>
                              <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff" }}>
                                <thead>
                                  <tr style={{ background: "#0f5f3b", color: "#fff" }}>
                                    <th style={{ padding: "12px", textAlign: "center", fontWeight: 700, fontSize: 12, borderRight: "1px solid #ddd", width: "40px" }}>
                                      <input
                                        type="checkbox"
                                        checked={selectedCourses.length > 0 && selectedCourses.length === courses.filter(c => getReservedCount(c.courseId) < (c.limit || 40)).length}
                                        onChange={handleSelectAll}
                                        style={{ cursor: "pointer" }}
                                      />
                                    </th>
                                    <th style={{ padding: "12px", textAlign: "left", fontWeight: 700, fontSize: 12, borderRight: "1px solid #ddd" }}>Subject Code</th>
                                    <th style={{ padding: "12px", textAlign: "left", fontWeight: 700, fontSize: 12, borderRight: "1px solid #ddd" }}>Subject Name</th>
                                    <th style={{ padding: "12px", textAlign: "left", fontWeight: 700, fontSize: 12, borderRight: "1px solid #ddd" }}>Schedule / Room</th>
                                    <th style={{ padding: "12px", textAlign: "center", fontWeight: 700, fontSize: 12, borderRight: "1px solid #ddd" }}>Units</th>
                                    <th style={{ padding: "12px", textAlign: "center", fontWeight: 700, fontSize: 12, borderRight: "1px solid #ddd" }}>Limit</th>
                                    <th style={{ padding: "12px", textAlign: "center", fontWeight: 700, fontSize: 12, borderRight: "1px solid #ddd" }}>Reserved</th>
                                    <th style={{ padding: "12px", textAlign: "center", fontWeight: 700, fontSize: 12 }}>Enrolled</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {courses.map((course) => {
                                    const reserved = getReservedCount(course.courseId);
                                    const enrolled = getEnrolledCount(course.courseId);
                                    const isFull = reserved >= (course.limit || 40);
                                    const isSelected = selectedCourses.includes(course.id);

                                    return (
                                      <tr
                                        key={course.id}
                                        style={{
                                          background: isSelected ? "#e3f2fd" : isFull ? "#ffebee" : "#fff",
                                          borderBottom: "1px solid #ddd",
                                          cursor: isFull && !isSelected ? "not-allowed" : "pointer",
                                          opacity: isFull && !isSelected ? 0.6 : 1
                                        }}
                                        onClick={() => !isFull || isSelected ? handleSelectCourse(course.id) : null}
                                      >
                                        <td style={{ padding: "12px", textAlign: "center", borderRight: "1px solid #ddd" }}>
                                          <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => {}}
                                            disabled={isFull && !isSelected}
                                            style={{ cursor: isFull && !isSelected ? "not-allowed" : "pointer" }}
                                          />
                                        </td>
                                        <td style={{ padding: "12px", borderRight: "1px solid #ddd", fontWeight: 600, fontSize: 13 }}>
                                          {course.courseId}
                                        </td>
                                        <td style={{ padding: "12px", borderRight: "1px solid #ddd", fontSize: 13 }}>
                                          {course.courseName}
                                        </td>
                                        <td style={{ padding: "12px", borderRight: "1px solid #ddd", fontSize: 12 }}>
                                          {(course.meetings || []).map((m, idx) => (
                                            <div key={idx} style={{ marginBottom: 4 }}>
                                              <div>{m.day} {m.time}</div>
                                              <div style={{ color: "#666" }}>Room: {m.room}</div>
                                            </div>
                                          ))}
                                        </td>
                                        <td style={{ padding: "12px", textAlign: "center", borderRight: "1px solid #ddd", fontWeight: 600, fontSize: 13 }}>
                                          {course.units}
                                        </td>
                                        <td style={{ padding: "12px", textAlign: "center", borderRight: "1px solid #ddd", fontWeight: 600, fontSize: 13 }}>
                                          {course.limit || 40}
                                        </td>
                                        <td style={{ padding: "12px", textAlign: "center", borderRight: "1px solid #ddd", fontWeight: 600, fontSize: 13, color: reserved >= (course.limit || 40) ? "#d9534f" : "#0f5f3b" }}>
                                          {reserved}
                                        </td>
                                        <td style={{ padding: "12px", textAlign: "center", fontWeight: 600, fontSize: 13, color: enrolled >= (course.limit || 40) ? "#d9534f" : "#0f5f3b" }}>
                                          {enrolled}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>

                            <button
                              onClick={handleAddCourses}
                              disabled={selectedCourses.length === 0}
                              style={{
                                width: "100%",
                                padding: "12px",
                                background: selectedCourses.length === 0 ? "#ccc" : "#0f5f3b",
                                color: "#fff",
                                border: "none",
                                borderRadius: 6,
                                fontWeight: 700,
                                cursor: selectedCourses.length === 0 ? "not-allowed" : "pointer",
                                fontSize: 14
                              }}
                            >
                              ADD ({selectedCourses.length})
                            </button>
                          </div>
                        )}

                        {selectedSection && courses.length === 0 && (
                          <div style={{ padding: 24, textAlign: "center", color: "#666" }}>
                            No courses available for this section
                          </div>
                        )}
                      </div>
                    )}

                    {/* Class List (Moved Below) */}
                    <div ref={classListRef} style={{ background: "#fff", borderRadius: 12, padding: "32px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
                      <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 16, color: "#0f5f3b" }}>
                        Class List {submittedTermKey ? `(${submittedTermKey.replace("_", " • ")})` : ""}
                      </div>

                      {message && !isSubmittedForTerm && (
                        <div style={{
                          padding: 12,
                          borderRadius: 6,
                          marginBottom: 16,
                          background: message.includes("✓") ? "#d4edda" : message.includes("Error") ? "#f8d7da" : "#fff3cd",
                          color: message.includes("✓") ? "#155601" : message.includes("Error") ? "#721c24" : "#856404",
                          fontSize: 14
                        }}>
                          {message}
                        </div>
                      )}

                      <div style={{ maxHeight: "400px", overflowY: "auto", border: "1px solid #e0e0e0", borderRadius: 8, marginBottom: 16 }}>
                        {(isSubmittedForTerm ? lockedClassList : classList).length === 0 ? (
                          <div style={{ padding: 24, textAlign: "center", color: "#999" }}>
                            No courses added yet
                          </div>
                        ) : (
                          (isSubmittedForTerm ? lockedClassList : classList).map((course, idx) => (
                            <div
                              key={idx}
                              style={{
                                padding: 12,
                                borderBottom: "1px solid #e0e0e0",
                                fontSize: 13,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center"
                              }}
                            >
                              <div>
                                <div style={{ fontWeight: 600, marginBottom: 4 }}>{course.courseId}</div>
                                <div style={{ color: "#666", fontSize: 12, marginBottom: 6 }}>
                                  {course.courseName}
                                </div>
                                {(course.meetings || []).map((m, i) => (
                                  <div key={i} style={{ color: "#888", fontSize: 11 }}>
                                    {m.day} {m.time} • Room: {m.room}
                                  </div>
                                ))}
                              </div>
                              {!isSubmittedForTerm && (
                                <button
                                  onClick={() => handleRemoveFromClassList(idx)}
                                  style={{
                                    background: "#f5f5f5",
                                    border: "1px solid #ddd",
                                    borderRadius: 4,
                                    padding: "6px 10px",
                                    cursor: "pointer",
                                    color: "#d9534f",
                                    fontWeight: 600,
                                    fontSize: 11
                                  }}
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          ))
                        )}
                      </div>

                      {/* Summary */}
                      <div style={{ background: "#f8fafb", borderRadius: 8, padding: 16, marginBottom: 16, border: "1px solid #e0e0e0" }}>
                        <div style={{ fontSize: 14, marginBottom: 8 }}>
                          <b>Total Courses:</b> {uniqueCourseCount}
                        </div>
                        <div style={{ fontSize: 14, marginBottom: 8 }}>
                          <b>Total Units:</b> {totalUnits}
                        </div>
                        <div style={{ fontSize: 13, color: "#666" }}>
                          Class List Items: {(isSubmittedForTerm ? lockedClassList : classList).length}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      {!isSubmittedForTerm && (
                        <div style={{ display: "flex", gap: 8 }}>
                          <button
                            onClick={() => {
                              setClassList([]);
                              setSelectedSection("");
                              setSelectedCourses([]);
                            }}
                            style={{
                              flex: 1,
                              padding: "10px",
                              background: "#f5f5f5",
                              border: "1px solid #ddd",
                              borderRadius: 6,
                              fontWeight: 600,
                              cursor: "pointer",
                              fontSize: 13
                            }}
                          >
                            Clear All
                          </button>
                          <button
                            onClick={handleSubmit}
                            disabled={classList.length === 0 || submitting}
                            style={{
                              flex: 1,
                              padding: "10px",
                              background: classList.length === 0 ? "#ccc" : "#0f5f3b",
                              color: "#fff",
                              border: "none",
                              borderRadius: 6,
                              fontWeight: 700,
                              cursor: classList.length === 0 ? "not-allowed" : "pointer",
                              fontSize: 13
                            }}
                          >
                            {submitting ? "Submitting..." : "SUBMIT"}
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}